/**
 * GrantCalendarDashboard
 *
 * The grant report calendar for a CBO running many overlapping grants
 * (e.g. Latina Contra Cancer: SVCN, Sacred Heart, Sobrato, Valley Foundation,
 * Destination Home). Lists report obligations with on-track / at-risk /
 * overdue / submitted indicators, lets staff mark a report submitted (which
 * advances recurring obligations to the next cycle), and links recurring
 * reports to their auto-generating scheduled report.
 *
 * Backend: app/routers/grant_obligations.py via services/api/grantObligations.
 */

import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle, AlertTriangle, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  GrantObligation,
  listGrantObligations,
  submitGrantObligation,
  updateGrantObligation,
} from '@/services/api/grantObligations';

function fmtCurrency(n?: number | null, currency = 'USD'): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n));
}

const RECURRENCE_LABEL: Record<string, string> = {
  one_time: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  monthly_within_days_of_month_end: 'Monthly (after month-end)',
};

function daysUntil(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const due = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

function statusBadge(o: GrantObligation): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } {
  if (o.status === 'submitted') return { label: 'Submitted', variant: 'secondary' };
  if (o.status === 'waived') return { label: 'Waived', variant: 'outline' };
  if (o.is_overdue) return { label: 'Overdue', variant: 'destructive' };
  const d = daysUntil(o.next_due_date);
  if (d !== null && d <= 7) return { label: `Due in ${d}d`, variant: 'destructive' };
  if (d !== null && d <= 30) return { label: `Due in ${d}d`, variant: 'default' };
  return { label: 'On track', variant: 'outline' };
}

export const GrantCalendarDashboard: React.FC = () => {
  const [rows, setRows] = useState<GrantObligation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editId, setEditId] = useState<number | null>(null);
  const [editAward, setEditAward] = useState('');
  const [editBudget, setEditBudget] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    listGrantObligations()
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load grant obligations.');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const handleSubmit = async (id: number) => {
    setBusyId(id);
    try {
      await submitGrantObligation(id);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to mark submitted.');
    } finally {
      setBusyId(null);
    }
  };

  const openBudgetEditor = (o: GrantObligation) => {
    setEditId(o.id);
    setEditAward(o.award_amount != null ? String(o.award_amount) : '');
    setEditBudget(o.total_budget != null ? String(o.total_budget) : '');
  };

  const saveBudget = async (id: number) => {
    setSavingBudget(true);
    try {
      await updateGrantObligation(id, {
        award_amount: editAward === '' ? null : Number(editAward),
        total_budget: editBudget === '' ? null : Number(editBudget),
      });
      setEditId(null);
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to save budget.');
    } finally {
      setSavingBudget(false);
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600">{error}</CardContent>
      </Card>
    );
  }
  if (rows === null) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-gray-500">Loading grant report calendar…</CardContent>
      </Card>
    );
  }

  const overdue = rows.filter((r) => r.status === 'pending' && r.is_overdue);
  const upcoming = rows.filter((r) => r.status === 'pending' && !r.is_overdue);
  const done = rows.filter((r) => r.status !== 'pending');

  const withBudget = rows.filter((r) => r.total_budget != null);
  const totalBudget = withBudget.reduce((s, r) => s + Number(r.total_budget || 0), 0);
  const totalSpent = withBudget.reduce((s, r) => s + Number(r.spent_to_date || 0), 0);
  const totalRemaining = totalBudget - totalSpent;

  const renderRow = (o: GrantObligation) => {
    const badge = statusBadge(o);
    const budget = o.total_budget != null ? Number(o.total_budget) : null;
    const spent = o.spent_to_date != null ? Number(o.spent_to_date) : 0;
    const remaining =
      o.budget_remaining != null ? Number(o.budget_remaining) : budget != null ? budget - spent : null;
    const pct = budget && budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
    const over = budget != null && spent > budget;
    const editing = editId === o.id;
    const ccy = o.currency || 'USD';
    return (
      <div key={o.id} className="border-b py-3 last:border-b-0">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{o.grant_name}</span>
              <Badge variant={badge.variant}>{badge.label}</Badge>
              {o.fund_restriction ? <Badge variant="outline">{o.fund_restriction}</Badge> : null}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {o.report_type ? `${o.report_type} · ` : ''}
              {o.funder ? `${o.funder} · ` : ''}
              {RECURRENCE_LABEL[o.recurrence] || o.recurrence}
              {o.next_due_date ? ` · due ${o.next_due_date}` : ''}
              {o.submitter_name ? ` · ${o.submitter_name}` : ''}
              {o.scheduled_report_id ? ' · auto-report linked' : ''}
            </div>
            {o.notes ? <div className="text-xs text-gray-400 mt-0.5 truncate">{o.notes}</div> : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="ghost" onClick={() => (editing ? setEditId(null) : openBudgetEditor(o))}>
              {editing ? 'Close' : 'Budget'}
            </Button>
            {o.status === 'pending' ? (
              <Button size="sm" variant="outline" disabled={busyId === o.id} onClick={() => handleSubmit(o.id)}>
                {busyId === o.id ? 'Saving…' : 'Mark submitted'}
              </Button>
            ) : (
              <span className="text-xs text-gray-400">
                {o.submitted_date ? `Submitted ${o.submitted_date}` : 'Submitted'}
              </span>
            )}
          </div>
        </div>

        {budget != null && !editing ? (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Award {fmtCurrency(o.award_amount, ccy)} · Budget {fmtCurrency(budget, ccy)}
              </span>
              <span className={over ? 'text-red-600 font-medium' : ''}>
                Spent {fmtCurrency(spent, ccy)}
                {remaining != null ? ` · ${fmtCurrency(remaining, ccy)} left` : ''}
              </span>
            </div>
            <div className="mt-1 h-2 w-full rounded bg-gray-100 overflow-hidden">
              <div className={`h-full ${over ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        ) : null}

        {editing ? (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">
              Award
              <input
                type="number"
                value={editAward}
                onChange={(e) => setEditAward(e.target.value)}
                className="ml-1 w-28 rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="0"
              />
            </label>
            <label className="text-xs text-gray-600">
              Budget
              <input
                type="number"
                value={editBudget}
                onChange={(e) => setEditBudget(e.target.value)}
                className="ml-1 w-28 rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="0"
              />
            </label>
            <Button size="sm" disabled={savingBudget} onClick={() => saveBudget(o.id)}>
              {savingBudget ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {withBudget.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4" /> Grant financials ({withBudget.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold">{fmtCurrency(totalBudget)}</div>
                <div className="text-xs text-gray-500">Budgeted</div>
              </div>
              <div>
                <div className="text-lg font-semibold">{fmtCurrency(totalSpent)}</div>
                <div className="text-xs text-gray-500">Spent</div>
              </div>
              <div>
                <div className={`text-lg font-semibold ${totalRemaining < 0 ? 'text-red-600' : ''}`}>
                  {fmtCurrency(totalRemaining)}
                </div>
                <div className="text-xs text-gray-500">Remaining</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {overdue.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700 text-base">
              <AlertTriangle className="h-4 w-4" /> Overdue ({overdue.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">{overdue.map(renderRow)}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4" /> Upcoming reports ({upcoming.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {upcoming.length ? upcoming.map(renderRow) : <div className="text-sm text-gray-500 py-3">Nothing upcoming.</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-gray-600">
            <CheckCircle className="h-4 w-4" /> Submitted / waived ({done.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {done.length ? done.map(renderRow) : <div className="text-sm text-gray-500 py-3 flex items-center gap-2"><Clock className="h-3 w-3" /> None yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default GrantCalendarDashboard;
